/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect, type ElementType } from 'react';
import classnames from 'classnames';
import Collapsible from 'components/Collapsible/Collapsible';
import scrollTo from 'utils/scrollTo';
import useGuid from 'effects/useGuid';
import '../Ensemble.scss';
import './Band.scss';

const DEFAULT_LIST: NonNullable<BandProps['list']> = [];

interface BandProps {
  id?: string | number | null;
  level?: number;
  heading?: string;
  list?: { id: string | number; instrument: string; performer: string }[];
  container?: Element | null;
}

/**
 * This is the Band component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Band({
  id = null,
  level = 3,
  heading = 'Band',
  list = DEFAULT_LIST,
  container = null,
}: BandProps) {
  const guid = useGuid('ensemble-band');
  const node = useRef<HTMLElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const [focus, setFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const Header = `h${level}` as ElementType;

  useEffect(() => {
    function onFocus() {
      if (!node.current!.contains(document.activeElement)) {
        setFocus(false);
      }
    }

    if (focus && open) {
      document.body.addEventListener('focusin', onFocus);
    }

    return () => {
      document.body.removeEventListener('focusin', onFocus);
    };
  }, [focus, open]);

  useEffect(() => {
    function onChange() {
      if (`#${id}` === window.location.hash) {
        setOpen(true);
      }
    }

    window.addEventListener('hashchange', onChange);
    onChange();

    return () => {
      window.removeEventListener('hashchange', onChange);
    };
  }, [id]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    function onOpen() {
      timeout = setTimeout(() => {
        setOpen(true);
      }, 1000);
    }

    if (container) {
      container.addEventListener('open', onOpen);
    }

    return function cleanup() {
      clearTimeout(timeout);
      if (container) {
        container.removeEventListener('open', onOpen);
      }
    };
  }, []);

  useEffect(() => {
    if (opened) {
      const showMenu = document.getElementById('show-sub-menu');
      if (showMenu) {
        scrollTo(node.current!, {
          alignTop: true,
          offset: showMenu.offsetHeight + 20,
        });
      } else {
        scrollTo(node.current!, { alignTop: true, offset: 180 });
      }
    }
  }, [opened]);

  return (
    <section
      ref={node}
      className={classnames('ensemble', 'band', { open })}
      onFocus={() => {
        setFocus(true);
      }}
    >
      <button
        ref={button}
        id={`button${guid}`}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={`details${guid}`}
        onClick={() => {
          setOpen(open => !open);
        }}
      >
        <Header className="h3">
          <span />
          {heading}
        </Header>
      </button>
      <Collapsible
        component="dl"
        id={`details${guid}`}
        role="region"
        aria-labelledby={`button${guid}`}
        active={focus && open}
        open={open}
        onOpen={() => {
          setOpened(true);
        }}
        onClose={() => {
          setOpen(false);
          if (button.current) {
            button.current.focus();
          }
        }}
        onClosed={() => {
          setOpened(false);
        }}
      >
        {list.map(({ id, instrument, performer }) => (
          <div key={id}>
            <dt>{instrument}</dt>
            <dd>{performer}</dd>
          </div>
        ))}
      </Collapsible>
    </section>
  );
}

export default Band;
